/**
 * MarkShippedDialog: the super-admin "Mark as fixed" control (v0.17.15).
 *
 * For a fix the GitHub bridge cannot see (a merged PR that was never linked,
 * or a data repair). It moves the work item to "Shipped, awaiting your
 * confirmation" so the reporter can Confirm or say it is Still broken. It
 * never confirms the fix for the reporter. The email box starts unchecked:
 * by default nobody is emailed.
 *
 * Backend: POST /api/coordination/work-items/{id}/mark-shipped
 * (routers/coordination_view.py::mark_shipped_endpoint).
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { portalWriteError } from '@/lib/portal/mutationError';

const SHA_RE = /^[0-9a-fA-F]{7,40}$/;
const TAG_RE = /^v\d+(\.\d+){1,3}([-+][0-9A-Za-z.-]{1,40})?$/;

export function MarkShippedDialog({
  workItemId,
  open,
  onOpenChange,
  onDone,
}: {
  workItemId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: () => void | Promise<void>;
}) {
  const [pr, setPr] = useState('');
  const [sha, setSha] = useState('');
  const [tag, setTag] = useState('');
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(false);

  const prValue = pr.trim().replace(/^#/, '');
  const prOk = prValue === '' || /^\d+$/.test(prValue);
  const shaOk = sha.trim() === '' || SHA_RE.test(sha.trim());
  const tagOk = tag.trim() === '' || TAG_RE.test(tag.trim());
  const hasEvidence = [prValue, sha.trim(), tag.trim(), note.trim()].some((v) => v !== '');
  const valid = hasEvidence && prOk && shaOk && tagOk;

  const reset = () => {
    setPr('');
    setSha('');
    setTag('');
    setNote('');
    setNotify(false);
  };

  const mutation = useMutation({
    mutationFn: () =>
      api.coordination.markShipped(workItemId, {
        ...(prValue ? { pr_number: Number(prValue) } : {}),
        ...(sha.trim() ? { commit_sha: sha.trim() } : {}),
        ...(tag.trim() ? { release_tag: tag.trim() } : {}),
        ...(note.trim() ? { data_repair_note: note.trim() } : {}),
        notify_reporters: notify,
      }),
    onSuccess: async (result) => {
      await onDone();
      toast.success(
        result.reporters_notified
          ? 'Marked as fixed. We emailed the reporter.'
          : 'Marked as fixed. No email was sent.'
      );
      reset();
      onOpenChange(false);
    },
    onError: (err: unknown) => toast.error(portalWriteError(err, 'Could not mark this as fixed')),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent data-testid="coord-mark-shipped-dialog">
        <DialogHeader>
          <DialogTitle>Mark as fixed</DialogTitle>
          <DialogDescription>
            Use this when a fix went out without a linked PR, or when you fixed the data by hand.
            The reporter will see &quot;Shipped, awaiting your confirmation&quot; and can confirm it
            or tell us it is still broken. Only the reporter can confirm it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">Give at least one of these.</p>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="ship-pr">Merged PR number</Label>
              <Input
                id="ship-pr"
                data-testid="coord-ship-pr"
                inputMode="numeric"
                placeholder="341"
                value={pr}
                onChange={(e) => setPr(e.target.value)}
                aria-invalid={!prOk}
              />
              {!prOk && <p className="text-xs text-destructive">Use a number, like 341.</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="ship-sha">Commit</Label>
              <Input
                id="ship-sha"
                data-testid="coord-ship-sha"
                placeholder="1f6e9059"
                value={sha}
                onChange={(e) => setSha(e.target.value)}
                aria-invalid={!shaOk}
              />
              {!shaOk && <p className="text-xs text-destructive">Use 7 to 40 letters a to f and digits.</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="ship-tag">Release</Label>
              <Input
                id="ship-tag"
                data-testid="coord-ship-tag"
                placeholder="v0.17.14"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                aria-invalid={!tagOk}
              />
              {!tagOk && <p className="text-xs text-destructive">Use a tag like v0.17.14.</p>}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="ship-note">What we repaired (if there is no PR)</Label>
            <Textarea
              id="ship-note"
              data-testid="coord-ship-note"
              rows={3}
              maxLength={2000}
              placeholder="Fixed the totals on invoice 1042 by hand."
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <div className="flex items-start gap-2">
            <Checkbox
              id="ship-notify"
              data-testid="coord-ship-notify"
              checked={notify}
              onCheckedChange={(v) => setNotify(v === true)}
            />
            <Label htmlFor="ship-notify" className="text-sm font-normal leading-snug">
              Email the reporter now. Leave this off to tell them yourself.
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            data-testid="coord-ship-submit"
            disabled={!valid || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Saving…' : 'Mark as fixed'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
